/**
 * Outward/inset offset for closed SVG paths (from grid blob outlines) via Clipper.
 * Coordinates are in the same user space as the luminance grid (1 unit = one cell edge).
 */

import type { Point } from "./gridBlobOutline";
import ClipperLib from "clipper-lib";

const CLIPPER_SCALE = 100_000;

/** Parse paths produced by gridBlobOutline (`M x y L x y ... Z`). */
function parseSimpleClosedPathD(d: string): Point[] | null {
    const trimmed = d.trim().replace(/Z\s*$/i, "");
    const nums = trimmed.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi);
    if (!nums || nums.length < 4) return null;
    const pts: Point[] = [];
    let i = 0;
    const head = trimmed.slice(0, 1).toUpperCase();
    if (head === "M") {
        pts.push([parseFloat(nums[0]), parseFloat(nums[1])]);
        i = 2;
    } else {
        return null;
    }
    while (i + 1 < nums.length) {
        pts.push([parseFloat(nums[i]), parseFloat(nums[i + 1])]);
        i += 2;
    }
    if (pts.length < 3) return null;
    return pts;
}

function toClipperPath(pts: Point[]) {
    return pts.map(
        ([x, y]) =>
            new ClipperLib.IntPoint2(
                Math.round(x * CLIPPER_SCALE),
                Math.round(y * CLIPPER_SCALE)
            )
    );
}

function clipperPathToPathD(path: { X: number; Y: number }[]): string {
    if (path.length < 2) return "";
    const fmt = (n: number) => {
        const x = n / CLIPPER_SCALE;
        const s = x.toFixed(6);
        return s.replace(/\.?0+$/, "") || "0";
    };
    let d = `M${fmt(path[0].X)} ${fmt(path[0].Y)}`;
    for (let i = 1; i < path.length; i++) {
        d += `L${fmt(path[i].X)} ${fmt(path[i].Y)}`;
    }
    d += "Z";
    return d;
}

/**
 * Positive delta moves the outline outward (away from filled cells); negative insets.
 * Delta is in SVG user units (same as viewBox: 1 = one cell width).
 */
export function offsetClosedPathDs(
    pathDs: string[],
    deltaUser: number
): string[] {
    if (deltaUser === 0 || pathDs.length === 0) return pathDs;

    const deltaScaled = deltaUser * CLIPPER_SCALE;
    const arcTol = Math.max(
        0.25 * CLIPPER_SCALE,
        Math.abs(deltaScaled) * 0.0025
    );
    const out: string[] = [];

    for (const d of pathDs) {
        const pts = parseSimpleClosedPathD(d);
        if (!pts) continue;

        const co = new ClipperLib.ClipperOffset(2, arcTol);
        co.AddPath(
            toClipperPath(pts),
            ClipperLib.JoinType.jtRound,
            ClipperLib.EndType.etClosedPolygon
        );
        const solution = new ClipperLib.Paths();
        co.Execute(solution, deltaScaled);

        if (solution.length === 0) {
            continue;
        }
        for (let i = 0; i < solution.length; i++) {
            const pd = clipperPathToPathD(solution[i]);
            if (pd) out.push(pd);
        }
    }

    return out.length > 0 ? out : pathDs;
}
